// skipcq: JS-W1028
import { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useBeerIngestionRuns from '../../hooks/useBeerIngestionRuns';
import {
  getBeerIngestionRun,
  retryBeerIngestionProcessing,
} from '../../services/breweryBeerSourcesService';
import {
  BEER_INGESTION_RUN_LIST_LIMIT,
  INGESTION_RUN_STATUSES,
} from '../../utils/callableBeerConstants';
import {
  captureBeerIngestionRunRetry,
  captureBeerIngestionRunView,
  captureBeerIngestionReviewOpen,
} from '../../utils/beerIngestionAnalytics';
import {
  canRetryIngestionRun,
  collectIngestionRunKinds,
  collectIngestionRunSourceTypes,
  collectIngestionRunTriggers,
  filterBeerIngestionRuns,
  INGESTION_RUN_FILTER_ALL,
} from '../../utils/brewery/beerIngestionRunFilters';
import BeerIngestionRunDetailDialog from './BeerIngestionRunDetailDialog';
import {
  BeerIngestionRunListPanel,
  IngestionRunsFilterBar,
} from './breweryBeerIngestionRunPanels';

function unwrapRunDetail(payload) {
  if (payload?.run && typeof payload.run === 'object') {
    return {
      run: payload.run,
      snapshot: payload.snapshot ?? null,
      comparison: payload.comparison ?? null,
      review: payload.review ?? null,
    };
  }
  return {
    run: payload ?? null,
    snapshot: null,
    comparison: null,
    review: null,
  };
}

function BreweryBeerIngestionRunsSection({ parentBreweryId, breweryName = '' }) {
  const { runs, userNamesById, loading, error, refresh } = useBeerIngestionRuns(parentBreweryId);
  const [statusFilter, setStatusFilter] = useState(INGESTION_RUN_FILTER_ALL);
  const [triggerFilter, setTriggerFilter] = useState(INGESTION_RUN_FILTER_ALL);
  const [sourceTypeFilter, setSourceTypeFilter] = useState(INGESTION_RUN_FILTER_ALL);
  const [runKindFilter, setRunKindFilter] = useState(INGESTION_RUN_FILTER_ALL);
  const [filterBreweryId, setFilterBreweryId] = useState(parentBreweryId);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [previousRun, setPreviousRun] = useState(null);
  const [previousSnapshot, setPreviousSnapshot] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewingRunId, setViewingRunId] = useState('');
  const [retryingRunId, setRetryingRunId] = useState('');
  const [actionError, setActionError] = useState(null);
  const detailRequestRef = useRef(0);

  if (filterBreweryId !== parentBreweryId) {
    detailRequestRef.current += 1;
    setFilterBreweryId(parentBreweryId);
    setStatusFilter(INGESTION_RUN_FILTER_ALL);
    setTriggerFilter(INGESTION_RUN_FILTER_ALL);
    setSourceTypeFilter(INGESTION_RUN_FILTER_ALL);
    setRunKindFilter(INGESTION_RUN_FILTER_ALL);
    setDetailOpen(false);
    setSelectedRun(null);
    setSelectedSnapshot(null);
    setSelectedComparison(null);
    setSelectedReview(null);
    setPreviousRun(null);
    setPreviousSnapshot(null);
    setViewingRunId('');
    setRetryingRunId('');
    setActionError(null);
  }

  const sourceTypeOptions = useMemo(
    () => collectIngestionRunSourceTypes(runs),
    [runs],
  );
  const triggerOptions = useMemo(
    () => collectIngestionRunTriggers(runs),
    [runs],
  );
  const runKindOptions = useMemo(
    () => collectIngestionRunKinds(runs),
    [runs],
  );

  const filteredRuns = useMemo(
    () =>
      filterBeerIngestionRuns(runs, {
        statusFilter,
        triggerFilter,
        sourceTypeFilter,
        runKindFilter,
      }),
    [runs, statusFilter, triggerFilter, sourceTypeFilter, runKindFilter],
  );

  const listEmptyMessage = useMemo(() => {
    if (runs.length === 0) {
      return 'No ingestion runs for this brewery.';
    }
    if (filteredRuns.length === 0) {
      return 'No ingestion runs match the current filter.';
    }
    return undefined;
  }, [runs.length, filteredRuns.length]);

  const openRunDetail = useCallback(async (run) => {
    const requestBreweryId = parentBreweryId;
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    const isStaleRequest = () => (
      requestBreweryId !== parentBreweryId || detailRequestRef.current !== requestId
    );
    setActionError(null);
    setViewingRunId(run.id);
    setSelectedRun(null);
    setSelectedSnapshot(null);
    setSelectedComparison(null);
    setSelectedReview(null);
    setPreviousRun(null);
    setPreviousSnapshot(null);
    setDetailOpen(true);
    captureBeerIngestionRunView(run.id, requestBreweryId);
    if (run.hasComparison) {
      captureBeerIngestionReviewOpen(run.id, requestBreweryId, 'brewery_runs');
    }
    try {
      const detail = unwrapRunDetail(await getBeerIngestionRun(run.id));
      if (isStaleRequest()) {
        return;
      }
      setSelectedRun(detail.run);
      setSelectedSnapshot(detail.snapshot);
      setSelectedComparison(detail.comparison);
      setSelectedReview(detail.review);
      setPreviousRun(null);
      setPreviousSnapshot(null);
      if (detail.run?.previousRunId) {
        try {
          const priorDetail = unwrapRunDetail(
            await getBeerIngestionRun(detail.run.previousRunId),
          );
          if (isStaleRequest()) {
            return;
          }
          setPreviousRun(priorDetail.run);
          setPreviousSnapshot(priorDetail.snapshot);
        } catch {
          // Previous-run context is optional when the link cannot be loaded.
        }
      }
    } catch (detailError) {
      if (isStaleRequest()) {
        return;
      }
      setDetailOpen(false);
      setActionError(detailError?.message || 'Failed to load ingestion run');
    } finally {
      if (!isStaleRequest()) {
        setViewingRunId('');
      }
    }
  }, [parentBreweryId]);

  const handleViewRun = useCallback((run) => {
    openRunDetail(run);
  }, [openRunDetail]);

  const handleRetryRun = useCallback(async (run) => {
    if (!canRetryIngestionRun(run)) {
      return;
    }
    const requestBreweryId = parentBreweryId;
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    const isStaleRequest = () => (
      requestBreweryId !== parentBreweryId || detailRequestRef.current !== requestId
    );
    setActionError(null);
    setRetryingRunId(run.id);
    captureBeerIngestionRunRetry(run.id, parentBreweryId);
    try {
      const retriedRun = await retryBeerIngestionProcessing(run.id);
      if (requestBreweryId === parentBreweryId) {
        refresh();
      }
      if (isStaleRequest()) {
        return;
      }
      setSelectedRun(retriedRun);
      setSelectedSnapshot(null);
      setSelectedComparison(null);
      setSelectedReview(null);
      setPreviousRun(null);
      setPreviousSnapshot(null);
      setDetailOpen(true);
      try {
        const detail = unwrapRunDetail(await getBeerIngestionRun(retriedRun.id));
        if (isStaleRequest()) {
          return;
        }
        if (detail.run) {
          setSelectedRun(detail.run);
          setSelectedSnapshot(detail.snapshot);
          setSelectedComparison(detail.comparison);
          setSelectedReview(detail.review);
        }
      } catch {
        // Retry already succeeded; snapshot can stay pending in the dialog.
      }
    } catch (retryError) {
      if (isStaleRequest()) {
        return;
      }
      setActionError(retryError?.message || 'Failed to retry ingestion processing');
    } finally {
      setRetryingRunId('');
    }
  }, [parentBreweryId, refresh]);

  const handleCloseDetail = useCallback(() => {
    detailRequestRef.current += 1;
    setDetailOpen(false);
    setSelectedRun(null);
    setSelectedSnapshot(null);
    setSelectedComparison(null);
    setSelectedReview(null);
    setPreviousRun(null);
    setPreviousSnapshot(null);
    setViewingRunId('');
    setRetryingRunId('');
  }, []);

  const handleOpenPreviousRun = useCallback((priorRun) => {
    setSelectedRun(priorRun);
    setSelectedSnapshot(previousSnapshot);
    setSelectedComparison(null);
    setSelectedReview(null);
    setPreviousRun(null);
    setPreviousSnapshot(null);
  }, [previousSnapshot]);

  const handleReviewUpdated = useCallback((nextRun, nextReview) => {
    if (nextRun) {
      setSelectedRun(nextRun);
    }
    if (nextReview) {
      setSelectedReview(nextReview);
    }
    refresh();
  }, [refresh]);

  const handleStatusChange = useCallback((event) => {
    setStatusFilter(event.target.value);
  }, []);

  const handleTriggerChange = useCallback((event) => {
    setTriggerFilter(event.target.value);
  }, []);

  const handleSourceTypeChange = useCallback((event) => {
    setSourceTypeFilter(event.target.value);
  }, []);

  const handleRunKindChange = useCallback((event) => {
    setRunKindFilter(event.target.value);
  }, []);

  const handleSelectedRunRetry = useCallback(() => {
    if (selectedRun && canRetryIngestionRun(selectedRun)) {
      handleRetryRun(selectedRun);
    }
  }, [selectedRun, handleRetryRun]);

  const showRecentCapCaption = runs.length >= BEER_INGESTION_RUN_LIST_LIMIT;

  return (
    <Box sx={{ p: 2 }}>
      <IngestionRunsFilterBar
        statusFilter={statusFilter}
        triggerFilter={triggerFilter}
        sourceTypeFilter={sourceTypeFilter}
        runKindFilter={runKindFilter}
        statusOptions={INGESTION_RUN_STATUSES}
        triggerOptions={triggerOptions}
        sourceTypeOptions={sourceTypeOptions}
        runKindOptions={runKindOptions}
        onStatusChange={handleStatusChange}
        onTriggerChange={handleTriggerChange}
        onSourceTypeChange={handleSourceTypeChange}
        onRunKindChange={handleRunKindChange}
      />
      {actionError ? (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {actionError}
        </Typography>
      ) : null}
      {showRecentCapCaption ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Showing the {BEER_INGESTION_RUN_LIST_LIMIT} most recent runs.
        </Typography>
      ) : null}
      <BeerIngestionRunListPanel
        runs={filteredRuns}
        userNamesById={userNamesById}
        loading={loading}
        error={error}
        emptyMessage={listEmptyMessage}
        onViewRun={handleViewRun}
        viewingRunId={viewingRunId}
      />
      <BeerIngestionRunDetailDialog
        open={detailOpen}
        run={selectedRun}
        snapshot={selectedSnapshot}
        comparison={selectedComparison}
        review={selectedReview}
        breweryId={parentBreweryId}
        breweryName={breweryName}
        previousRun={previousRun}
        loading={Boolean(viewingRunId)}
        retryLoading={Boolean(retryingRunId)}
        onClose={handleCloseDetail}
        onOpenPreviousRun={handleOpenPreviousRun}
        onReviewUpdated={handleReviewUpdated}
        onRetry={
          selectedRun && canRetryIngestionRun(selectedRun)
            ? handleSelectedRunRetry
            : undefined
        }
      />
    </Box>
  );
}

BreweryBeerIngestionRunsSection.propTypes = {
  parentBreweryId: PropTypes.string.isRequired,
  breweryName: PropTypes.string,
};

export default BreweryBeerIngestionRunsSection;
